import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/ui';
import { getDepth, getFoldList } from './data';
import { Depth } from './depth';
import { AGING_META, reconOf } from './display';
import { CashflowFilter, FoldListResponse } from './types';
import { ExecBrief } from './components/ExecBrief';
import { FeedbackDialog, FeedbackTarget } from './components/FeedbackDialog';
import { InvoiceList } from './components/InvoiceList';
import { InvoiceDetail } from './components/InvoiceDetail';

/**
 * Cash Flow Dashboard (v2). Two-stage fetch (spec §6): /recon/fold paints
 * instantly (tiles + rows); /recon/master lands later and lights up the depth
 * (aging, activity, extra lenses, deep-review). Filters from the Exec Brief
 * apply client-side. Screen 2 is the slide-over detail.
 */
export const CashflowDashboard: React.FC = () => {
	const [resp, setResp] = useState<FoldListResponse | null>(null);
	const [depth, setDepth] = useState<Depth | null>(null);
	const [depthFailed, setDepthFailed] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<CashflowFilter | null>(null);
	const [open, setOpen] = useState<string | null>(null);
	const [feedback, setFeedback] = useState<FeedbackTarget | null>(null);
	const [flagged, setFlagged] = useState<Set<string>>(new Set());

	useEffect(() => {
		let active = true;
		getFoldList({ limit: 1000 })
			.then(x => active && setResp(x))
			.catch(e => active && setError(e?.message || 'Failed to load'))
			.finally(() => active && setLoading(false));
		getDepth()
			.then(x => active && setDepth(x))
			.catch(() => active && setDepthFailed(true));
		return () => {
			active = false;
		};
	}, []);

	const rows = useMemo(() => {
		if (!resp) return [];
		let r = resp.invoices;
		if (filter?.recon) r = r.filter(x => x.recon_class === filter.recon);
		if (filter?.aging) r = r.filter(x => depth?.by.get(x.invoice_number)?.aging === filter.aging);
		if (filter?.audit) r = r.filter(x => depth?.by.get(x.invoice_number)?.labels?.includes(filter.audit!));
		return r;
	}, [resp, filter, depth]);

	const filterLabel = filter?.recon
		? reconOf(filter.recon).label
		: filter?.aging
			? `aging ${AGING_META[filter.aging].label}`
			: filter?.audit === 'audit_disagreement'
				? 'audit corrects'
				: filter?.audit === 'audit_misclassified'
					? 'misclassified'
					: null;

	return (
		<div className='flex h-full flex-col bg-[#f7f9fb] p-6 [font-family:Inter,sans-serif]'>
			<PageHeader title='Cash Flow' totalItems={0} itemType='analysis' icon='💸' />

			{resp && (
				<p className='-mt-3 mb-5 text-sm text-slate-400'>
					<span className='font-semibold tabular-nums text-slate-700'>{resp.tiles.n}</span> invoices ·{' '}
					AI-verified payment truth from the email trail
				</p>
			)}

			{loading ? (
				<div className='flex flex-1 flex-col items-center justify-center text-slate-400'>
					<Loader2 className='mb-3 h-8 w-8 animate-spin' />
					<p className='text-sm'>Loading cash-flow analysis…</p>
				</div>
			) : error ? (
				<div className='rounded-lg border border-red-200 bg-red-50 p-4'>
					<div className='font-medium text-red-800'>Could not load</div>
					<div className='mt-1 text-sm text-red-700'>{error}</div>
				</div>
			) : resp ? (
				<div className='space-y-6'>
					<ExecBrief tiles={resp.tiles} summary={depth?.summary ?? null} activeFilter={filter} onFilter={setFilter} />
					{depthFailed && (
						<div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800'>
							Detail data unavailable — aging, activity and extra lenses are off. Reload to retry.
						</div>
					)}
					{filterLabel && (
						<div className='flex items-center gap-2 text-sm'>
							<span className='font-medium text-slate-600'>Filtered: {filterLabel} · {rows.length}</span>
							<button onClick={() => setFilter(null)} className='rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600 hover:bg-slate-200'>
								clear ✕
							</button>
						</div>
					)}
					<InvoiceList
						rows={rows}
						depth={depth?.by ?? null}
						onOpen={setOpen}
						flagged={flagged}
						onFlag={r =>
							setFeedback({ row: r, depthRow: depth?.by.get(r.invoice_number) ?? null, surface: 'list' })
						}
					/>
				</div>
			) : null}

			<InvoiceDetail
				invoiceNumber={open}
				depthRow={open ? (depth?.by.get(open) ?? null) : null}
				onFlag={threadId => {
					const r = resp?.invoices.find(x => x.invoice_number === open);
					if (r) setFeedback({ row: r, depthRow: depth?.by.get(r.invoice_number) ?? null, threadId, surface: 'drawer' });
				}}
				onClose={() => setOpen(null)}
			/>

			<FeedbackDialog
				target={feedback}
				activeFilter={filterLabel}
				onClose={() => setFeedback(null)}
				onSubmitted={inv => setFlagged(prev => new Set(prev).add(inv))}
			/>
		</div>
	);
};

export default CashflowDashboard;
